import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const AgentPerformanceTable = ({ agents }) => {
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedAgents = [...(agents || [])]?.sort((a, b) => {
    let aVal = a?.[sortField];
    let bVal = b?.[sortField];

    if (typeof aVal === 'string') {
      aVal = aVal?.toLowerCase();
      bVal = bVal?.toLowerCase();
    }

    if (sortDirection === 'asc') {
      return aVal > bVal ? 1 : -1;
    } else {
      return aVal < bVal ? 1 : -1;
    }
  });

  const getRoleColor = (role) => {
    const colors = {
      coordinator: 'var(--color-primary)',
      validator: 'var(--color-success)',
      analyst: 'var(--color-accent)',
      auditor: 'var(--color-warning)',
      specialist: 'var(--color-error)'
    };
    return colors?.[role] || 'var(--color-muted-foreground)';
  };

  const getPerformanceIndicator = (value, threshold) => {
    if (value >= threshold) {
      return { icon: 'TrendingUp', color: 'var(--color-success)' };
    } else if (value >= threshold * 0.7) {
      return { icon: 'Minus', color: 'var(--color-warning)' };
    } else {
      return { icon: 'TrendingDown', color: 'var(--color-error)' };
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) {
      return <Icon name="ChevronsUpDown" size={14} color="var(--color-muted-foreground)" />;
    }
    return sortDirection === 'asc' 
      ? <Icon name="ChevronUp" size={14} color="var(--color-primary)" />
      : <Icon name="ChevronDown" size={14} color="var(--color-primary)" />;
  };

  return (
    <div className="card">
      <div className="flex items-center gap-3 mb-6">
        <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-lg">
          <Icon name="BarChart3" size={20} color="var(--color-primary)" />
        </div>
        <h2 className="text-xl font-semibold font-mono">Agent Performance Metrics</h2>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border">
              <th 
                className="text-left py-3 px-4 text-sm font-medium text-muted-foreground cursor-pointer hover:text-foreground transition-colors"
                onClick={() => handleSort('name')}
              >
                <div className="flex items-center gap-2">
                  Agent Name
                  <SortIcon field="name" />
                </div>
              </th>
              <th 
                className="text-left py-3 px-4 text-sm font-medium text-muted-foreground cursor-pointer hover:text-foreground transition-colors"
                onClick={() => handleSort('role')}
              >
                <div className="flex items-center gap-2">
                  Role
                  <SortIcon field="role" />
                </div>
              </th>
              <th 
                className="text-center py-3 px-4 text-sm font-medium text-muted-foreground cursor-pointer hover:text-foreground transition-colors"
                onClick={() => handleSort('avgResponseTime')}
              >
                <div className="flex items-center justify-center gap-2">
                  Avg Response Time
                  <SortIcon field="avgResponseTime" />
                </div>
              </th>
              <th 
                className="text-center py-3 px-4 text-sm font-medium text-muted-foreground cursor-pointer hover:text-foreground transition-colors"
                onClick={() => handleSort('completionRate')}
              >
                <div className="flex items-center justify-center gap-2">
                  Completion Rate
                  <SortIcon field="completionRate" />
                </div>
              </th>
              <th 
                className="text-center py-3 px-4 text-sm font-medium text-muted-foreground cursor-pointer hover:text-foreground transition-colors"
                onClick={() => handleSort('accuracyScore')}
              >
                <div className="flex items-center justify-center gap-2">
                  Accuracy Score
                  <SortIcon field="accuracyScore" />
                </div>
              </th>
              <th 
                className="text-center py-3 px-4 text-sm font-medium text-muted-foreground cursor-pointer hover:text-foreground transition-colors"
                onClick={() => handleSort('successRate')}
              >
                <div className="flex items-center justify-center gap-2">
                  Success Rate
                  <SortIcon field="successRate" />
                </div>
              </th>
              <th 
                className="text-center py-3 px-4 text-sm font-medium text-muted-foreground cursor-pointer hover:text-foreground transition-colors"
                onClick={() => handleSort('totalAnalyses')}
              >
                <div className="flex items-center justify-center gap-2">
                  Total Analyses
                  <SortIcon field="totalAnalyses" />
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedAgents?.map((agent) => {
              const completionIndicator = getPerformanceIndicator(agent?.completionRate, 80);
              const accuracyIndicator = getPerformanceIndicator(agent?.accuracyScore, 90);
              const successIndicator = getPerformanceIndicator(agent?.successRate, 85);

              return (
                <tr key={agent?.id} className="border-b border-border hover:bg-accent/5 transition-colors">
                  <td className="py-4 px-4">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                        <Icon name="User" size={16} color="var(--color-primary)" />
                      </div>
                      <div>
                        <p className="font-medium text-foreground">{agent?.name}</p>
                        <p className="text-xs text-muted-foreground">{agent?.specialization?.[0] || 'General'}</p>
                      </div>
                    </div>
                  </td>
                  <td className="py-4 px-4">
                    <span 
                      className="inline-flex items-center px-2 py-1 rounded text-xs font-medium"
                      style={{ 
                        backgroundColor: `${getRoleColor(agent?.role)}15`,
                        color: getRoleColor(agent?.role)
                      }}
                    >
                      {agent?.role}
                    </span>
                  </td>
                  <td className="py-4 px-4 text-center">
                    <span className="text-foreground font-medium">{agent?.avgResponseTime}s</span>
                  </td>
                  <td className="py-4 px-4 text-center">
                    <div className="flex items-center justify-center gap-2">
                      <Icon name={completionIndicator?.icon} size={16} color={completionIndicator?.color} />
                      <span className="text-foreground font-medium">{agent?.completionRate}%</span>
                    </div>
                  </td>
                  <td className="py-4 px-4 text-center">
                    <div className="flex items-center justify-center gap-2">
                      <Icon name={accuracyIndicator?.icon} size={16} color={accuracyIndicator?.color} />
                      <span className="text-foreground font-medium">{agent?.accuracyScore}%</span>
                    </div>
                  </td>
                  <td className="py-4 px-4 text-center">
                    <div className="flex items-center justify-center gap-2">
                      <Icon name={successIndicator?.icon} size={16} color={successIndicator?.color} />
                      <span className="text-foreground font-medium">{agent?.successRate}%</span>
                    </div>
                  </td>
                  <td className="py-4 px-4 text-center">
                    <span className="text-foreground font-medium">{agent?.totalAnalyses}</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AgentPerformanceTable;